import { useGizmo } from "flowy-3-core";
import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  regulaToBlinkResult,
  backendResponseToBlink,
  getImagesFromResponse,
  getContainerList,
  REGULA_GRAPHIC,
  getFieldListFromContainer,
  RESULT_IMAGES,
  blinkLikeToBinderPayloadAsync,
  logOCRResult,
} from "./regulaToBlinkResult";
import transformFiles from "../FileField/transformFiles";
import { Trash, Camera, Upload, CheckCircle, Loader2 } from "lucide-react";
import { GizmoWrapper } from "../../utils";
import { InternalScenarios } from '@regulaforensics/vp-frontend-document-components';
import "./idOcrField.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://kong-cluster.wsflowz.com";

//prod
// const REGULA_LICENSE = "AAEAAG/VPbxGf9Bqf7kz6KTKpQCutS5vxeZiCxEQM8B4ql8Xf+kJ6HshcihKlWbtg7xa78JdAfBv3Hmxj668To2/oOmXcU1gVbJWN0/bfHwcoOmmK2yZ9Nnan7XO7nuXBJ/8AgJMH+ZDw4+dIO+Pg6ANGfSNWcj98oPigxH3I7snodUw0c8wx5Zl9VogPis8OYQaJFuEj2KBchyL5TfT/kGDirIZqLYNYMEHv6GUB8XbKWWNN3d6yCxyED/mkKnBnrkwt4wpef3L6A8PeU7t4VbTSEKZhDEPNuSY3ryqqkHuhKg9c9q1QMTeTFqjzNZj1uFEySFQPRCSyCEsj3hCO3u9A1zkAAAAAAAAEDPx5oRFsKVdDyABYj6I0jejHbnFOzmJZZu5NfO5oObaHW0sbcldI4WIfJM9Frogi3xu7O+mkI6ul9fB5wTxNRQQr4i0tKDiJyzNhjLusf6loyN4McthdbGZQ1JXuz/4tFi/85KLOPYnr1VE8SkUD+cb84QVuCm+202eWoCZyPyD9AYzKlqpmcQLHpF/yAzqvZsYDAu3q1ByGJzNLGeYuHovhRhQt0u3wuPArT+7swWyLpSSGoysrVCLpguYjz0xeArs2p8OOqEPgv9NbMlLcrNvQY5mZwz3Dloh5jp2wiap"
//dev
// const REGULA_LICENSE = "AAEAAHPZiHJ7sDOLpKvzNcCQg+U/jSRA/UseOMivNGsW7mKCuYgeSxczjeqAQVkSFgtXUsnaq9f7K2N/jxtqHZgFTOE0RVNP2gQ6qftqNg2HCkW0dr4kSkj+wR7e6MpJsqjWn1d+sE0QljLlVNQMhGeY+EZHyvBCqzdJayMSJzQYV4dfPlureAc5I+7biU3V47k1jdBF0n3K79EUzRQpmQefYGTUjJrDfMgIGxOKlge3kO+8ek84b4Vt6LH4+5tUp3sA5YYkSjH0gei/k4mQb4IQrMNPoBHS8WKbuom2dg9H3kYnngxRQLbO56ltIhoBxwtXni2Fiuf5uILL7yZovOaQuTHkAAAAAAAAECPiVaFY6MBOmGT8gnBfKyzZxd/ojlpyjdd6PtKyOGCxVDBzvYWL3xpF4kwo7RXC/gSYwVL3ja2hzqyPoCqk1tVQEhv34Dr71rDx7fEwmCx6NBG0/RsBw30bwbkMrpZ5I//5170ghCbzH5RfLpcrp12gi/We9ijqX8K9vziBsgjeGctZbJ8b0aVN9BXMn9V8yNLVPBPVQGtPXMW0W/9MQx1KLMMBx0R+L0vIt9E5gW1kK7DIK/CwyW73aJ82TOST5stzm6wRnv+3OuyYzbcL0PCy8Mxhv8bbHTHMqqEq0mse"

const REGULA_LICENSE = false

const EVENT_ACTIONS = { PROCESS_FINISHED: "PROCESS_FINISHED", CLOSE: "CLOSE" };

const IdOcrFieldRegula = ({ gizmo }) => {
  const { features, binder, errors } = useGizmo({ gizmo });
  const containerRef = useRef(null);
  const readerRef = useRef(null);
  const ocrImagesRef = useRef([]);
  const ocrLastResponseRef = useRef(null);
  const ocrSessionIdRef = useRef(0);
  const ocrSawDocFrontRef = useRef(false);
  const ocrSawDocBackRef = useRef(false);
  const ocrDocFrontB64Ref = useRef("");
  const ocrDocBackB64Ref = useRef("");
  const uploadFrontRef = useRef(null);
  const uploadBackRef = useRef(null);
  const lastFrontFileRef = useRef(null);
  const lastBackFileRef = useRef(null);
  const [frontFileList, setFrontFileList] = useState([]);
  const [backFileList, setBackFileList] = useState([]);
  // const [onlyFront, setOnlyFront] = useState(false);
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [regulaReady, setRegulaReady] = useState(false);
  const [showReader, setShowReader] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const config = gizmo.getConfig();
  const useFront = config?.ops?.idOcrField?.personalIdOptions?.onlyFront;

  const resetOcrBuffers = useCallback(() => {
    ocrImagesRef.current = [];
    ocrLastResponseRef.current = null;
    ocrSessionIdRef.current += 1;
    ocrSawDocFrontRef.current = false;
    ocrSawDocBackRef.current = false;
    ocrDocFrontB64Ref.current = "";
    ocrDocBackB64Ref.current = "";
  }, []);

  const sendImagesToBackend = useCallback(
    async (images) => {
      const url = `${BACKEND_URL}/api/verify`;
      const payload = { images };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || res.statusText);
      }
      return res.json();
    },
    []
  );

  const handleRegulaResult = useCallback(
    async (event) => {
      const action = event?.detail?.action;
      const data = event?.detail?.data;
      const status = data?.status;
      const response = data?.response;

      if (action === EVENT_ACTIONS.CLOSE || action === "CLOSE") {
        setShowReader(false);
        resetOcrBuffers();
        return;
      }

      if (
        action !== EVENT_ACTIONS.PROCESS_FINISHED &&
        action !== "PROCESS_FINISHED"
      )
        return;
      if (!response || !binder) return;

      const isFinishStatus = status === 1 || status === 2;
      if (!isFinishStatus) return;

      const morePagesAvailable =
        !!(response?.rawResponse?.morePagesAvailable ?? response?.morePagesAvailable);
      const isLastPage = !morePagesAvailable;

      // Accumulate images across all PROCESS_FINISHED events (front/back).
      ocrLastResponseRef.current = response;
      try {
        const normB64 = (s) =>
          s && typeof s === "string"
            ? s.replace(/^data:image\/\w+;base64,/, "").trim()
            : "";

        // Extract full document images (graphic 207/208) and disambiguate sides by page index.
        // In multipage mode, some docs return DOCUMENT_FRONT (207) on both pages; in that case,
        // treat page_idx=0 as front and page_idx=1 as back.
        const containerList = getContainerList(response);
        if (Array.isArray(containerList) && containerList.length) {
          for (const container of containerList) {
            const rt =
              container?.result_type ?? container?.ResultType ?? container?.resultType;
            if (Number(rt) !== Number(RESULT_IMAGES)) continue;
            const pageIdxRaw =
              container?.page_idx ?? container?.pageIndex ?? container?.PageIndex;
            const pageIdx =
              pageIdxRaw === undefined || pageIdxRaw === null
                ? undefined
                : Number(pageIdxRaw);
            const fieldList = getFieldListFromContainer(container, true);
            for (const field of fieldList) {
              const fieldType = Number(field?.fieldType ?? field?.FieldType ?? field?.type);
              if (
                fieldType !== Number(REGULA_GRAPHIC.DOCUMENT_FRONT) &&
                fieldType !== Number(REGULA_GRAPHIC.DOCUMENT_REAR)
              )
                continue;
              const valueList = field?.valueList ?? field?.ValueList ?? [];
              const first = Array.isArray(valueList) && valueList.length ? valueList[0] : field;
              const v = normB64(first?.value ?? first?.Value ?? field?.value ?? field?.Value);
              if (!v) continue;
              if (fieldType === Number(REGULA_GRAPHIC.DOCUMENT_FRONT)) {
                ocrSawDocFrontRef.current = true;
                if (pageIdx === 1) {
                  // 207 but on page 2 => use as back
                  ocrSawDocBackRef.current = true;
                  if (!ocrDocBackB64Ref.current) ocrDocBackB64Ref.current = v;
                } else {
                  if (!ocrDocFrontB64Ref.current) ocrDocFrontB64Ref.current = v;
                }
              } else if (fieldType === Number(REGULA_GRAPHIC.DOCUMENT_REAR)) {
                ocrSawDocBackRef.current = true;
                if (!ocrDocBackB64Ref.current) ocrDocBackB64Ref.current = v;
              }
            }
          }
        }

        const pageImages = getImagesFromResponse(response)?.map(normB64) ?? [];
        if (pageImages?.length) {
          const merged = [...(ocrImagesRef.current ?? []), ...pageImages].filter(Boolean);
          // Dedupe (the SDK can repeat the same base64 across events).
          ocrImagesRef.current = Array.from(new Set(merged));
        }
      } catch (e) {
        // If parsing images fails, we'll still try to build OCR result from response.
        console.warn("IdOcrField Regula: failed to collect images from page:", e);
      }

      // Don't send to backend until we have both sides (for multipage),
      // or until last page if we're only scanning the front.
      const needsBothSides = !useFront;
      const hasRequiredSides = needsBothSides
        ? ocrSawDocFrontRef.current && ocrSawDocBackRef.current
        : ocrSawDocFrontRef.current;

      const imgCount = (ocrImagesRef.current ?? []).filter(Boolean).length;
      const hasMinImages = needsBothSides ? imgCount >= 2 : imgCount >= 1;
      const shouldSendNow = hasRequiredSides || (isLastPage && hasMinImages);

      if (!shouldSendNow) {
        if (isLastPage) {
          setShowReader(false);
          resetOcrBuffers();
          setUploadError(
            "No se pudieron obtener las imágenes necesarias. Intenta de nuevo o carga las fotos manualmente."
          );
        }
        return;
      }

      const sessionAtStart = ocrSessionIdRef.current;

      setUploadLoading(true);
      setShowReader(false);

      try {
        // Best-effort: finalize package for backend-reprocessing workflows.
        try {
          window.RegulaDocumentSDK?.finalizePackage?.();
        } catch (e) {
          console.warn("IdOcrField Regula: finalizePackage failed:", e);
        }

        // IMPORTANT: For backend cropping, send only the full document images (207/208)
        // in a stable order: [front, back]. Sending the whole images container (barcode, portrait, etc)
        // can cause the backend to crop only the first one and mis-assign sides.
        const docFront = ocrDocFrontB64Ref.current;
        const docBack = ocrDocBackB64Ref.current;
        const fallbackUnique = Array.from(
          new Set((ocrImagesRef.current ?? []).filter(Boolean))
        );
        const preferred = [];
        if (docFront) preferred.push(docFront);
        if (docBack) preferred.push(docBack);
        const filled = [
          ...preferred,
          ...fallbackUnique.filter((b64) => !preferred.includes(b64)),
        ];
        // Build Regula "bundle" (up to 4) but only send the cropped-ready ones:
        // per your observation, indices 0 and 2 are the correct front/back crops.
        const bundle = filled.slice(0, 4);
        const images = [bundle[0], bundle[2]].filter(Boolean);
        const finalResponse = ocrLastResponseRef.current ?? response;

        if (images.length === 0) {
          logOCRResult(finalResponse);
          const blinkLike = regulaToBlinkResult(finalResponse);
          const payload = await blinkLikeToBinderPayloadAsync(blinkLike);
          await binder.setOcrResult(payload);
        } else {
          const backendData = await sendImagesToBackend(images);
          console.log("Backend verify response:", backendData);
          logOCRResult(backendData?.result ?? backendData, {
            croppedImages: backendData?.croppedImages,
          });
          const blinkLike = backendResponseToBlink(backendData);
          console.log("blinkLike:", blinkLike);
          const normB64 = (s) =>
            s && typeof s === "string"
              ? s.replace(/^data:image\/\w+;base64,/, "").trim()
              : "";

          // UI: show exactly what we sent (front/back).
          const sent0 = normB64(images?.[0]);
          const sent1 = normB64(images?.[1]);
          if (sent0) {
            blinkLike.fullDocumentFrontImage = sent0;
          }
          if (sent1) {
            blinkLike.fullDocumentBackImage = sent1;
          }
          if (!blinkLike.fullDocumentImage && blinkLike.fullDocumentFrontImage) {
            blinkLike.fullDocumentImage = blinkLike.fullDocumentFrontImage;
          }

          // Keep backend croppedImages as fallback only (do not override deterministic UI images).
          if (!blinkLike.fullDocumentFrontImage && backendData?.croppedImages?.front) {
            blinkLike.fullDocumentFrontImage =
              normB64(backendData.croppedImages.front) || backendData.croppedImages.front;
          }
          if (!blinkLike.fullDocumentBackImage && backendData?.croppedImages?.back) {
            blinkLike.fullDocumentBackImage =
              normB64(backendData.croppedImages.back) || backendData.croppedImages.back;
          }
          if (!blinkLike.fullDocumentImage && blinkLike.fullDocumentFrontImage) {
            blinkLike.fullDocumentImage = blinkLike.fullDocumentFrontImage;
          }
          const payload = await blinkLikeToBinderPayloadAsync(blinkLike);
          await binder.setOcrResult(payload);
        }

        // If the user re-opened the reader mid-flight, don't clobber their new session's UI.
        if (sessionAtStart !== ocrSessionIdRef.current) return;

        const files = binder.getFiles();
        if (files?.front) {
          transformFiles([files.front]).then((res) => setFrontFileList(res));
        }
        if (files?.back) {
          transformFiles([files.back]).then((res) => setBackFileList(res));
        }
        resetOcrBuffers();
      } catch (err) {
        console.error("IdOcrField Regula result handling:", err);
        setUploadError("Error al procesar el escaneo. Intenta de nuevo.");
      } finally {
        setUploadLoading(false);
      }
    },
    [binder, sendImagesToBackend, resetOcrBuffers, useFront]
  );

  const createDocumentReader = useCallback(() => {
    const el = document.createElement("document-reader");
    el.settings = {
      startScreen: false,
      changeCameraButton: false,
      closeButton: false,
      newLayout: true,

      // startScreen: true,
      // changeCameraButton: true,
      // newLayout: true,
      internalScenario: 'Locate',
      multipageProcessing: true,
    };
    const h = "100dvh";
    el.style.cssText = `display: block; width: 100%; min-height: ${h}; height: ${h};`;
    return el;
  }, []);

  const loadRegulaScript = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (window.Regula?.defineComponents && window.Regula?.DocumentReaderService) {
          resolve();
          return;
        }
        if (document.querySelector('script[src*="vp-frontend-document-components"]')) {
          const check = () => {
            if (window.Regula?.defineComponents) resolve();
            else setTimeout(check, 100);
          };
          check();
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://unpkg.com/@regulaforensics/vp-frontend-document-components@9.2.2688/dist/main.iife.js";
        script.async = true;
        script.onload = () => {
          if (window.Regula?.defineComponents) resolve();
          else reject(new Error("Regula script loaded but window.Regula not found"));
        };
        script.onerror = () => reject(new Error("Failed to load Regula script"));
        document.head.appendChild(script);
      }),
    []
  );

  const initializeRegula = useCallback(() => {
    if (typeof window === "undefined") return;

    loadRegulaScript()
      .then(() => {
        const { defineComponents, DocumentReaderService } = window.Regula;

        if (typeof DocumentReaderService !== "function") {
          console.error("Regula: DocumentReaderService not found");
          return;
        }
        if (typeof defineComponents !== "function") {
          console.error("Regula: defineComponents not found");
          return;
        }

        window.RegulaDocumentSDK = new DocumentReaderService();
        const processParam = {
          scenario: InternalScenarios.Locate,
          multipageProcessing: !useFront,
          log: true
        };
        window.RegulaDocumentSDK.recognizerProcessParam = { processParam };
        window.RegulaDocumentSDK.imageProcessParam = {
          processParam: { scenario: InternalScenarios.Locate, multipageProcessing: true },
        };

        return defineComponents()
          .then(() =>
            REGULA_LICENSE
              ? window.RegulaDocumentSDK.initialize({ license: REGULA_LICENSE })
              : window.RegulaDocumentSDK.initialize()
          )
          .then(() => setRegulaReady(true));
      })
      .catch((err) => console.error("Regula init failed:", err));
  }, [useFront, loadRegulaScript]);

  useEffect(() => {
    if (!showReader) return;
    if (typeof window === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showReader]);

  useEffect(() => {
    if (!showReader || !regulaReady || !containerRef.current) return;
    const container = containerRef.current;
    const inner = container.querySelector(".regula-reader-slot") ?? container;
    const wrapper = document.createElement("div");
    wrapper.className = "regula-reader-wrapper";
    const h = "100dvh";
    wrapper.style.cssText = `width: 100%; min-height: ${h}; height: ${h}; position: relative; overflow: visible;`;
    const readerEl = createDocumentReader();
    readerRef.current = readerEl;
    const handler = (e) => handleRegulaResult(e);
    readerEl.addEventListener("document-reader", handler);
    wrapper.appendChild(readerEl);
    inner.appendChild(wrapper);

    // Hide "Powered by Regula" if rendered inside shadow DOM (best-effort).
    const BRANDING_STYLE_ID = "__hide_regula_powered_by__";
    const applyHideBranding = () => {
      let changed = false;

      // 1) If shadowRoot is open, inject CSS inside it (most reliable).
      const root = readerEl?.shadowRoot;
      if (root && !root.getElementById?.(BRANDING_STYLE_ID)) {
        const style = document.createElement("style");
        style.id = BRANDING_STYLE_ID;
        style.textContent = `
          [part="container-footer"],
          [part="container-copyright"],
          [data-e2e="powered-by"] {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
          }
        `;
        root.appendChild(style);
        changed = true;
      }

      // 2) Also hide via direct attribute selectors (works for light DOM or open shadow queries).
      const candidates = [];
      try {
        candidates.push(
          ...readerEl.querySelectorAll?.(
            '[part="container-footer"],[part="container-copyright"],[data-e2e="powered-by"]'
          )
        );
      } catch {
        // no-op
      }
      if (root) {
        try {
          candidates.push(
            ...root.querySelectorAll?.(
              '[part="container-footer"],[part="container-copyright"],[data-e2e="powered-by"]'
            )
          );
        } catch {
          // no-op
        }
      }
      for (const el of candidates) {
        if (el && el.style?.display !== "none") {
          el.style.display = "none";
          el.style.visibility = "hidden";
          changed = true;
        }
      }

      return changed;
    };

    // Regula re-renderiza; re-aplicamos por un rato y observamos cambios.
    let tries = 0;
    const t = window.setInterval(() => {
      tries += 1;
      applyHideBranding();
      if (tries >= 30) window.clearInterval(t);
    }, 200);

    const observerRoot = readerEl?.shadowRoot ?? readerEl;
    const mo =
      observerRoot && typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => applyHideBranding())
        : null;
    mo?.observe?.(observerRoot, { childList: true, subtree: true });

    return () => {
      try {
        readerEl.removeEventListener("document-reader", handler);
      } catch {
        // no-op
      }
      if (readerRef.current === readerEl) readerRef.current = null;
      window.clearInterval(t);
      try {
        mo?.disconnect?.();
      } catch {
        // no-op
      }
      wrapper.remove();
    };
  }, [showReader, regulaReady, createDocumentReader, handleRegulaResult]);

  useEffect(() => {
    if (binder) {
      // const onlyFrontVal = binder.getOnlyFront();
      // if (onlyFrontVal !== undefined) setOnlyFront(onlyFrontVal);
      const files = binder.getFiles();
      if (files?.front) {
        transformFiles([files.front]).then((res) => setFrontFileList(res));
      }
      if (files?.back) {
        transformFiles([files.back]).then((res) => setBackFileList(res));
      }
      if (features?.idOcrField?.scannerEnabled === false) {
        setScannerEnabled(false);
      }
    }
  }, [binder, features?.idOcrField?.scannerEnabled]);

  useEffect(() => {
    const initialFeatures = gizmo.getFeatures();
    if (initialFeatures.editable === true) {
      initializeRegula();
    }
    const sub = gizmo.features.subscribe((feat) => {
      if (feat.editable === true) initializeRegula();
    });
    return () => sub?.unsubscribe?.();
  }, [gizmo, initializeRegula]);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const normB64 = (s) =>
  (s && typeof s === "string"
    ? s.replace(/^data:image\/\w+;base64,/, "").trim()
    : "");

  const sendBothImagesToBackend = useCallback(
    async (frontFile, backFile) => {
      if (!frontFile || !backFile) {
        setUploadError("Se necesitan ambas imágenes (frente y reverso).");
        return;
      }
      if (!binder) {
        setUploadError("No se puede enviar en este momento. Vuelve a intentar.");
        return;
      }
      setUploadError(null);
      setUploadLoading(true);
      try {
        const [dataUrl1, dataUrl2] = await Promise.all([
          fileToBase64(frontFile),
          fileToBase64(backFile),
        ]);
        const backendData = await sendImagesToBackend([
          normB64(dataUrl1),
          normB64(dataUrl2),
        ]);
        logOCRResult(backendData?.result ?? backendData, {
          croppedImages: backendData?.croppedImages,
        });
        const blinkLike = backendResponseToBlink(backendData);
        if (backendData?.croppedImages?.front) {
          blinkLike.fullDocumentFrontImage =
            normB64(backendData.croppedImages.front) ||
            backendData.croppedImages.front;
        }
        if (backendData?.croppedImages?.back) {
          blinkLike.fullDocumentBackImage =
            normB64(backendData.croppedImages.back) ||
            backendData.croppedImages.back;
        }
        if (!blinkLike.fullDocumentImage && blinkLike.fullDocumentFrontImage) {
          blinkLike.fullDocumentImage = blinkLike.fullDocumentFrontImage;
        }
        const payload = await blinkLikeToBinderPayloadAsync(blinkLike);
        await binder.setOcrResult(payload);
        const transformed = await transformFiles([frontFile, backFile]);
        if (transformed[0]) setFrontFileList([transformed[0]]);
        if (transformed[1]) setBackFileList([transformed[1]]);
      } catch (err) {
        console.error("[IdOcr] Error al cargar imágenes:", err);
        setUploadError("Error al procesar las imágenes. Intenta de nuevo.");
      } finally {
        setUploadLoading(false);
      }
    },
    [binder, sendImagesToBackend]
  );

  const handleFrontFileChange = useCallback(
    async (e) => {
      const input = e.target;
      const fileList = input.files;
      const file1 = fileList?.[0];
      const file2 = fileList?.[1];
      input.value = "";
      if (!file1) return;
      if (file1 && file2) {
        await sendBothImagesToBackend(file1, file2);
        return;
      }
      lastFrontFileRef.current = file1;
      const transformed = await transformFiles([file1]);
      if (transformed[0]) setFrontFileList([transformed[0]]);
      if (lastBackFileRef.current) {
        await sendBothImagesToBackend(lastFrontFileRef.current, lastBackFileRef.current);
      }
    },
    [sendBothImagesToBackend]
  );

  const handleBackFileChange = useCallback(
    async (e) => {
      const input = e.target;
      const fileList = input.files;
      const file1 = fileList?.[0];
      const file2 = fileList?.[1];
      input.value = "";
      if (!file1) return;
      if (file1 && file2) {
        await sendBothImagesToBackend(file1, file2);
        return;
      }
      lastBackFileRef.current = file1;
      const transformed = await transformFiles([file1]);
      if (transformed[0]) setBackFileList([transformed[0]]);
      if (lastFrontFileRef.current) {
        await sendBothImagesToBackend(lastFrontFileRef.current, lastBackFileRef.current);
      }
    },
    [sendBothImagesToBackend]
  );

  const handleImageClick = (imageList) => {
    if (imageList?.length > 0) {
      setModalImage(imageList[0]);
      setIsModalOpen(true);
    }
  };

  const handleDeleteScan = () => {
    setFrontFileList([]);
    setBackFileList([]);
    setUploadError(null);
    setScannerEnabled(true);
    lastFrontFileRef.current = null;
    lastBackFileRef.current = null;
  };

  const showScanner =
    features?.editable === true &&
    scannerEnabled === true &&
    frontFileList.length === 0 &&
    backFileList.length === 0;

  const openRegulaReader = () => {
    resetOcrBuffers();
    setUploadError(null);
    setShowReader(true);
  };

  return (
    <GizmoWrapper features={features} errors={errors}>
      <input
        ref={uploadFrontRef}
        type="file"
        accept="image/*"
        multiple={backFileList.length === 0}
        className="idoc-hidden"
        onChange={handleFrontFileChange}
        aria-hidden
      />
      <input
        ref={uploadBackRef}
        type="file"
        accept="image/*"
        multiple={frontFileList.length === 0}
        className="idoc-hidden"
        onChange={handleBackFileChange}
        aria-hidden
      />
      <div className="idoc-relative idoc-stack--spaced">
        {uploadLoading && (
          <div className="idoc-loading">
            <div className="idoc-loading__inner">
              <Loader2 className="idoc-loading__spinner" aria-hidden />
              <span className="idoc-loading__text">Cargando...</span>
            </div>
          </div>
        )}
        <div>
          {!showScanner ? (
            <div className="idoc-col-center">
              {frontFileList.length > 0 && backFileList.length > 0 ? (
                <>
                  <div className="idoc-row-thumbs">
                    <button
                      type="button"
                      className="idoc-thumb-btn"
                      onClick={() => handleImageClick(frontFileList)}
                    >
                      <img
                        src={frontFileList[0].thumbUrl}
                        alt="Frente"
                        className="idoc-thumb-img"
                      />
                    </button>
                    <button
                      type="button"
                      className="idoc-thumb-btn"
                      onClick={() => handleImageClick(backFileList)}
                    >
                      <img
                        src={backFileList[0].thumbUrl}
                        alt="Reverso"
                        className="idoc-thumb-img"
                      />
                    </button>
                  </div>
                  <div className="idoc-success-row">
                    <div className="idoc-success-msg">
                      <CheckCircle />
                      <span className="idoc-success-text">
                        La identificación se agregó correctamente
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleDeleteScan}
                      className="idoc-btn-link-danger"
                    >
                      <Trash size={18} />
                      Reemplazar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="idoc-card">
                    <div className="idoc-grid-2">
                      <div className="idoc-slot">
                        {frontFileList.length > 0 && frontFileList[0] ? (
                          <button
                            type="button"
                            className="idoc-slot-btn"
                            onClick={() => handleImageClick(frontFileList)}
                          >
                            <img
                              src={frontFileList[0].thumbUrl}
                              alt="Frente"
                              className="idoc-slot-img"
                            />
                          </button>
                        ) : (
                          <span className="idoc-slot-placeholder">
                            Frente
                          </span>
                        )}
                      </div>
                      <div className="idoc-slot">
                        {backFileList.length > 0 && backFileList[0] ? (
                          <button
                            type="button"
                            className="idoc-slot-btn"
                            onClick={() => handleImageClick(backFileList)}
                          >
                            <img
                              src={backFileList[0].thumbUrl}
                              alt="Reverso"
                              className="idoc-slot-img"
                            />
                          </button>
                        ) : (
                          <span className="idoc-slot-placeholder">
                            Reverso
                          </span>
                        )}
                      </div>
                    </div>
                    {(uploadError ||
                      frontFileList.length > 0 ||
                      backFileList.length > 0) && (
                        <p className="idoc-error-text">
                          {uploadError ||
                            "Falta una imagen de la identificación. Debes cargar el frente y el reverso para continuar."}
                        </p>
                      )}
                    <div className="idoc-actions">
                      <button
                        type="button"
                        disabled={uploadLoading}
                        onClick={openRegulaReader}
                        aria-label="Abrir scanner de Regula"
                        className="idoc-btn-primary"
                      >
                        <Camera aria-hidden />
                        Escanear con cámara
                      </button>
                      <div className="idoc-btn-row-2">
                        <button
                          type="button"
                          disabled={uploadLoading}
                          onClick={() => {
                            setUploadError(null);
                            uploadFrontRef.current?.click();
                          }}
                          className="idoc-btn-secondary"
                        >
                          <Upload />
                          Cargar frente
                        </button>
                        <button
                          type="button"
                          disabled={uploadLoading}
                          onClick={() => {
                            setUploadError(null);
                            uploadBackRef.current?.click();
                          }}
                          className="idoc-btn-secondary"
                        >
                          <Upload />
                          Cargar reverso
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isModalOpen && modalImage && (
                <div
                  className="idoc-modal-backdrop"
                  onClick={() => setIsModalOpen(false)}
                  role="presentation"
                >
                  <div
                    className="idoc-modal-panel"
                    onClick={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    <div className="idoc-modal-header">
                      <div>
                        <h2 className="idoc-modal-title">{modalImage.name}</h2>
                        <p className="idoc-modal-sub">
                          Vista previa de la imagen{" "}
                          {modalImage === frontFileList?.[0]
                            ? "frontal"
                            : "trasera"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(false)}
                        className="idoc-modal-close"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <img
                      src={modalImage.thumbUrl}
                      alt={modalImage.name}
                      className="idoc-modal-img"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : showReader ? (
            <div
              className="regula-reader-viewport idoc-reader-viewport"
              ref={containerRef}
            >
              <button
                type="button"
                onClick={() => setShowReader(false)}
                className="idoc-reader-close"
              >
                Cerrar
              </button>
              <div className="regula-reader-slot idoc-reader-slot" />
            </div>
          ) : (
            <div className="idoc-intro-wrap">
              <div className="idoc-intro-card">
                <div className="idoc-intro-icons">
                  <img
                    src="/icons/icon-id-card.png"
                    alt=""
                  />
                  <img
                    src="/icons/icon-document.png"
                    alt=""
                  />
                </div>
                <h2 className="idoc-intro-title">
                  Ingresa el frente y reverso de tu identificación
                </h2>
                <p className="idoc-intro-text">
                  Puedes cargar las imágenes desde tu dispositivo
                </p>
                <p className="idoc-intro-text idoc-intro-text--mb">
                  o utilizar la opción &quot;Escanear&quot;.
                </p>
                <div className="idoc-actions">
                  <button
                    type="button"
                    onClick={openRegulaReader}
                    aria-label="Abrir scanner de Regula para escanear identificación con cámara"
                    className="idoc-btn-primary"
                  >
                    <Camera aria-hidden />
                    Escanear con cámara
                  </button>
                  <div className="idoc-btn-row-2">
                    <button
                      type="button"
                      disabled={uploadLoading}
                      onClick={() => {
                        setUploadError(null);
                        uploadFrontRef.current?.click();
                      }}
                      className="idoc-btn-secondary"
                    >
                      <Upload />
                      Cargar frente
                    </button>
                    <button
                      type="button"
                      disabled={uploadLoading}
                      onClick={() => {
                        setUploadError(null);
                        uploadBackRef.current?.click();
                      }}
                      className="idoc-btn-secondary"
                    >
                      <Upload />
                      Cargar reverso
                    </button>
                  </div>
                </div>
                {uploadError && (
                  <p className="idoc-intro-error">
                    {uploadError}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </GizmoWrapper>
  );
};

export default IdOcrFieldRegula;
